import * as Sentry from '@sentry/browser';

/**
 * Returns Authorization header if user token is set
 *
 * @return {object} Empty object if no token is set, else X-CSRFToken header object
 */
const getAuthorizationHeader = () => {
    const token = Cookies.get('csrftoken');
    if (!token) {
        return {};
    }
    return {
        'X-CSRFToken': token,
    };
};

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response  A response from a network request
 * @return {object|Error}     Returns either the response, or throws an error
 */
const validateResponseStatus = (response) => {
    if (response.status === 403) {
        return new Error('Forbidden', { cause: response });
    }

    if (response.status === 404) {
        return new Error('Not Found', { cause: response });
    }

    if (response.status === 401) {
        return new Error('Unauthorized', { cause: response });
    }

    if (!response.ok) {
        const status = response.status || 'Unknown';
        const statusText = response.statusText?.trim() || 'No status text';
        const url = response.url || 'Unknown URL';

        const error = new Error(`HTTP ${status} - ${statusText} (${url})`);
        error.name = 'FetchError';
        error.response = response;
        error.status = response.status;

        Sentry.captureException(error, {
            extra: {
                status: response.status,
                statusText: response.statusText,
                url: response.url,
            },
        });

        throw error;
    }

    return response;
};

/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 * @return {object|null}     The parsed JSON from the request
 */
const parseResponseJSON = (response) => {
    if (response.status === 204 || response.status === 205) return null;

    if (response instanceof Error) {
        throw response;
    }

    return response.json();
};

/**
 * Parses the text response returned by a network request
 *
 * @param  {object} response Response stream
 * @return {object}          Parsed response
 */
const parseResponseText = (response) => response.text();

/**
 * Requests a URL, returning a promise
 * Catching an error should be handled by caller
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 * @return {object}           The response data
 */
export const fetchRequest = (url, options = {}) => {
    const authHeader = getAuthorizationHeader();
    const extendedOptions = {
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...authHeader,
        },
        method: options.method || 'GET',
        ...options,
    };

    return fetch(url, extendedOptions)
        .then(validateResponseStatus)
        .then(parseResponseJSON)
        .catch((err) => {
            // eslint-disable-next-line no-console
            console.error('Request failed:', err);
            throw err;
        });
};

const apiFetchRequest = (path, options) => fetchRequest(`/api/${path}`, options);

/**
 * Fetch HTML from url provided
 *
 * @param {string} url
 * @param {object} options
 */
export const documentFetchRequest = (url, options = {}) => {
    const authHeader = getAuthorizationHeader();
    const extendedOptions = {
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            ...authHeader,
        },
        method: options.method || 'GET',
        ...options,
    };

    return fetch(url, extendedOptions)
        .then(validateResponseStatus)
        .then(parseResponseText)
        .catch((err) => {
            // eslint-disable-next-line no-console
            console.error('Request failed:', err);
        });
};

/**
 * Create an URL by adding optional parameters to a URL base.
 *
 * @param {string} base
 * @param {key: boolean|number|string} params
 * @returns {string}
 */
export const buildURL = (base, params = {}) => {
    if (typeof params !== 'object' || Object.keys(params).length === 0) {
        return base;
    }

    const pairs = [];

    if (base.split('?').length > 1) {
        base.split('?')[1]
            .split('&')
            .forEach((x) => pairs.push(x));
    }

    Object.keys(params).forEach((key) => pairs.push(`${key}=${params[key].toString()}`));

    return `${base}?${pairs.join('&')}`;
};

export default apiFetchRequest;
